import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, Check, Mail, User, CheckCircle2, Circle, AlertCircle, Loader2 } from "lucide-react";
import { OnboardingData } from "../index";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Link } from "wouter";
import { apiRequest } from "@/lib/queryClient";

interface AccountCreationProps {
  onNext: () => void;
  onPrevious: () => void;
  onboardingData: OnboardingData;
  updateOnboardingData: (data: Partial<OnboardingData>) => void;
}

const accountCreationSchema = z.object({
  firstName: z.string().min(2, "First name is required"),
  lastName: z.string().min(2, "Last name is required"),
  email: z.string().email("Please enter a valid email address"),
  password: z.string()
    .min(8, "Password must be at least 8 characters")
    .max(100, "Password must be less than 100 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[0-9]/, "Password must contain at least one number")
    .regex(/[^A-Za-z0-9]/, "Password must contain at least one special character"),
  confirmPassword: z.string(),
  termsAccepted: z.boolean().refine(val => val === true, {
    message: "You must accept the terms and conditions",
  }),
  emailNotifications: z.object({
    reportsSummary: z.boolean().default(true),
    taskReminders: z.boolean().default(true),
    subscriptionAlerts: z.boolean().default(true),
    marketingEmails: z.boolean().default(false),
  }),
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

type AccountCreationFormValues = z.infer<typeof accountCreationSchema>;

// Function to check if a user with the given email already exists
const checkUserExists = async (email: string): Promise<boolean> => {
  try {
    const response = await apiRequest("POST", "/api/check-user-exists", { email });
    const data = await response.json();
    return data.exists;
  } catch (error) {
    console.error("Error checking if user exists:", error);
    return false;
  }
};

export default function AccountCreation({
  onNext,
  onPrevious,
  onboardingData,
  updateOnboardingData,
}: AccountCreationProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const emailCheckTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Debounced function to check if email exists
  const debouncedCheckEmail = async (email: string) => {
    if (!email || !email.includes('@')) return;
    
    // Clear any existing timer
    if (emailCheckTimerRef.current) {
      clearTimeout(emailCheckTimerRef.current);
    }
    
    // Set a new timer to check email after 500ms
    emailCheckTimerRef.current = setTimeout(async () => {
      if (!email || !email.includes('@')) return;
      
      setIsCheckingEmail(true);
      
      try {
        const response = await apiRequest("POST", "/api/check-user-exists", { email });
        const data = await response.json();
        setEmailExists(data.exists);
      } catch (error) {
        console.error("Failed to check email:", error);
      } finally {
        setIsCheckingEmail(false);
      }
    }, 500);
  };
  
  // Clear timer on unmount
  useEffect(() => {
    return () => {
      if (emailCheckTimerRef.current) {
        clearTimeout(emailCheckTimerRef.current);
      }
    };
  }, []);
  
  const form = useForm<AccountCreationFormValues>({
    resolver: zodResolver(accountCreationSchema),
    defaultValues: {
      firstName: onboardingData.firstName || "",
      lastName: onboardingData.lastName || "",
      email: onboardingData.email || "",
      password: "",
      confirmPassword: "",
      termsAccepted: false,
      emailNotifications: {
        reportsSummary: true,
        taskReminders: true,
        subscriptionAlerts: true, 
        marketingEmails: false,
      },
    },
  });
  
  const onSubmit = async (values: AccountCreationFormValues) => {
    try {
      // If we already know the email exists, prevent form submission
      if (emailExists) {
        toast({
          title: "Email already in use",
          description: "This email is already registered. Please use a different email or log in to your existing account.",
          variant: "destructive",
        });
        return;
      }
      
      setIsLoading(true);
      
      // Remove confirmPassword before saving
      const { confirmPassword, ...accountData } = values;
      
      // Do a final check if the email exists before proceeding
      const emailCheckRes = await apiRequest('POST', '/api/check-user-exists', { 
        email: accountData.email 
      });
      const emailCheckData = await emailCheckRes.json();
      
      if (emailCheckData.exists) {
        setEmailExists(true);
        toast({
          title: "Email already in use",
          description: "This email is already registered. Please use a different email or log in to your existing account.",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }
      
      // Store data in onboarding context
      updateOnboardingData({
        firstName: accountData.firstName,
        lastName: accountData.lastName,
        // Combine first and last name for fullName
        fullName: `${accountData.firstName} ${accountData.lastName}`,
        email: accountData.email,
        password: accountData.password, // Store actual password for registration
        emailNotifications: accountData.emailNotifications,
      });
      
      // Success notification
      toast({
        title: "Account details saved",
        description: "Your account information has been saved successfully.",
      });
      
      // Continue to next step
      onNext();
    } catch (error) {
      toast({
        title: "Error",
        description: "There was a problem creating your account. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="max-w-2xl mx-auto">
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold">Create Your ZentroBooks Account</h2>
          <p className="mt-2 text-neutral-600">
            Set up your account to get started with ZentroBooks
          </p>
        </div>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>First Name</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <User className="absolute left-3 top-3 h-4 w-4 text-neutral-400" />
                              <Input
                                placeholder="Enter your first name"
                                className="pl-9"
                                {...field}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Last Name</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <User className="absolute left-3 top-3 h-4 w-4 text-neutral-400" />
                              <Input
                                placeholder="Enter your last name"
                                className="pl-9"
                                {...field}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Address</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Mail className="absolute left-3 top-3 h-4 w-4 text-neutral-400" />
                            <Input
                              placeholder="Enter your email address"
                              type="email"
                              className={`pl-9 ${emailExists ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                // Reset emailExists when user changes the email
                                if (emailExists) {
                                  setEmailExists(false);
                                }
                                // Check email as user types (with debounce)
                                debouncedCheckEmail(e.target.value);
                              }}
                              onBlur={(e) => {
                                field.onBlur();
                                // Also check on blur for immediate feedback when tabbing/clicking away
                                const email = e.target.value;
                                
                                if (email && email.includes('@') && !isCheckingEmail) {
                                  // Clear any debounced checks
                                  if (emailCheckTimerRef.current) {
                                    clearTimeout(emailCheckTimerRef.current);
                                  }
                                  
                                  // Check immediately on blur
                                  setIsCheckingEmail(true);
                                  apiRequest("POST", "/api/check-user-exists", { email })
                                    .then(response => response.json())
                                    .then(data => {
                                      setEmailExists(data.exists);
                                    })
                                    .catch(error => {
                                      console.error("Failed to check email:", error);
                                    })
                                    .finally(() => {
                                      setIsCheckingEmail(false);
                                    });
                                }
                              }}
                            />
                            {isCheckingEmail && (
                              <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-neutral-500" />
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                        {emailExists && (
                          <Alert className="mt-2 border-red-500 bg-red-50 text-red-800">
                            <AlertCircle className="h-4 w-4 text-red-500" />
                            <AlertDescription className="flex items-center justify-between">
                              <span>This email is already registered.</span>
                              <Link to="/login" className="text-blue-600 font-medium ml-2 hover:underline">
                                Log in instead
                              </Link>
                            </AlertDescription>
                          </Alert>
                        )}
                      </FormItem>
                    )}
                  />
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Create a password"
                              type="password"
                              {...field}
                            />
                          </FormControl>
                          <div className="mt-1.5">
                            <p className="text-xs text-muted-foreground mb-1">Password must include:</p>
                            <ul className="text-xs text-muted-foreground space-y-1.5 mt-2">
                              <li className="flex items-center">
                                {field.value.length >= 8 ? (
                                  <CheckCircle2 className="h-3.5 w-3.5 mr-2 text-green-600" />
                                ) : (
                                  <Circle className="h-3.5 w-3.5 mr-2" />
                                )}
                                <span className={field.value.length >= 8 ? "text-green-600" : ""}>
                                  At least 8 characters
                                </span>
                              </li>
                              <li className="flex items-center">
                                {/[A-Z]/.test(field.value) ? (
                                  <CheckCircle2 className="h-3.5 w-3.5 mr-2 text-green-600" />
                                ) : (
                                  <Circle className="h-3.5 w-3.5 mr-2" />
                                )}
                                <span className={/[A-Z]/.test(field.value) ? "text-green-600" : ""}>
                                  At least one uppercase letter
                                </span>
                              </li>
                              <li className="flex items-center">
                                {/[a-z]/.test(field.value) ? (
                                  <CheckCircle2 className="h-3.5 w-3.5 mr-2 text-green-600" />
                                ) : (
                                  <Circle className="h-3.5 w-3.5 mr-2" />
                                )}
                                <span className={/[a-z]/.test(field.value) ? "text-green-600" : ""}>
                                  At least one lowercase letter
                                </span>
                              </li>
                              <li className="flex items-center">
                                {/[0-9]/.test(field.value) ? (
                                  <CheckCircle2 className="h-3.5 w-3.5 mr-2 text-green-600" />
                                ) : (
                                  <Circle className="h-3.5 w-3.5 mr-2" />
                                )}
                                <span className={/[0-9]/.test(field.value) ? "text-green-600" : ""}>
                                  At least one number
                                </span>
                              </li>
                              <li className="flex items-center">
                                {/[^A-Za-z0-9]/.test(field.value) ? (
                                  <CheckCircle2 className="h-3.5 w-3.5 mr-2 text-green-600" />
                                ) : (
                                  <Circle className="h-3.5 w-3.5 mr-2" />
                                )}
                                <span className={/[^A-Za-z0-9]/.test(field.value) ? "text-green-600" : ""}>
                                  At least one special character
                                </span>
                              </li>
                            </ul>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm Password</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Confirm your password"
                              type="password"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Communication Preferences</h3>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="emailNotifications.reportsSummary"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="font-medium">
                              Financial Reports Summary
                            </FormLabel>
                            <p className="text-sm text-neutral-500">
                              Receive regular summaries of your financial reports and insights
                            </p>
                          </div>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="emailNotifications.taskReminders"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="font-medium">
                              Task Reminders
                            </FormLabel>
                            <p className="text-sm text-neutral-500">
                              Get reminders for pending tasks and follow-ups
                            </p>
                          </div>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="emailNotifications.subscriptionAlerts"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="font-medium">
                              Subscription & Billing Alerts
                            </FormLabel>
                            <p className="text-sm text-neutral-500">
                              Important notifications about your subscription and billing
                            </p>
                          </div>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="emailNotifications.marketingEmails"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="font-medium">
                              Marketing & Product Updates
                            </FormLabel>
                            <p className="text-sm text-neutral-500">
                              Occasional newsletters about new features and promotions
                            </p>
                          </div>
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
              
              <FormField
                control={form.control}
                name="termsAccepted"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="font-medium">
                        I agree to the{" "}
                        <a href="#" className="text-primary hover:underline">
                          Terms of Service
                        </a>{" "}
                        and{" "}
                        <a href="#" className="text-primary hover:underline">
                          Privacy Policy
                        </a>
                      </FormLabel>
                    </div>
                  </FormItem>
                )}
              />
              <FormMessage>{form.formState.errors.termsAccepted?.message}</FormMessage>
            </div>
            
            <div className="flex justify-between pt-4">
              <Button type="button" variant="outline" onClick={onPrevious}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  "Saving..."
                ) : (
                  <>
                    Continue
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}